import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { slotsService } from '../../services/slotsService';
import { driversService } from '../../services/driversService';
import { SlotResponse } from '../../types/slots';
import { OrderAssignedDriverResponse } from '../../types/orders';
import { AdminDriversResponse } from '../../types/drivers';
import Modal from '../modals/Modal';
import './Page.css';

const formatTime = (epochMillis: number): string => {
  return new Date(epochMillis).toLocaleTimeString('uz-UZ', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tashkent'
  });
};

const formatMoney = (amount: number): string => {
  return amount.toLocaleString('uz-UZ') + ' so\'m';
};

const getTodayStr = (): string => {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tashkent' });
};

const epochToTimeInput = (epochMillis: number): string => {
  const d = new Date(epochMillis);
  const h = d.toLocaleString('uz-UZ', { hour: '2-digit', hour12: false, timeZone: 'Asia/Tashkent' }).padStart(2, '0');
  const m = d.toLocaleString('uz-UZ', { minute: '2-digit', timeZone: 'Asia/Tashkent' }).padStart(2, '0');
  return `${h}:${m}`;
};

const timeInputToEpoch = (dateStr: string, timeStr: string): number => {
  // Combine date + time as Tashkent local and convert to UTC epoch
  const [h, min] = timeStr.split(':').map(Number);
  const d = new Date(`${dateStr}T00:00:00+05:00`);
  d.setHours(d.getHours() + h, d.getMinutes() + min);
  // Re-parse properly: use date + time in +05:00
  const iso = `${dateStr}T${timeStr.padStart(5, '0')}:00+05:00`;
  return new Date(iso).getTime();
};

const Slots: React.FC = () => {
  const { t } = useTranslation();
  const todayStr = getTodayStr();

  const [slots, setSlots] = useState<SlotResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Create/Edit modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState<SlotResponse | null>(null);
  const [formDate, setFormDate] = useState('');
  const [formFromTime, setFormFromTime] = useState('09:00');
  const [formToTime, setFormToTime] = useState('18:00');
  const [formMinIncome, setFormMinIncome] = useState('');
  const [formPlaces, setFormPlaces] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete
  const [deletingSlotId, setDeletingSlotId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Driver management panel
  const [selectedSlot, setSelectedSlot] = useState<SlotResponse | null>(null);
  const [slotDrivers, setSlotDrivers] = useState<OrderAssignedDriverResponse[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driverSearchQuery, setDriverSearchQuery] = useState('');
  const [driverSearchResults, setDriverSearchResults] = useState<AdminDriversResponse[]>([]);
  const [driverSearchLoading, setDriverSearchLoading] = useState(false);
  const [driverSearchError, setDriverSearchError] = useState('');
  const [driverActionLoading, setDriverActionLoading] = useState(false);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  useEffect(() => {
    if (selectedSlot) {
      fetchSlotDrivers(selectedSlot.id);
    } else {
      setSlotDrivers([]);
    }
    setDriverSearchQuery('');
    setDriverSearchResults([]);
  }, [selectedSlot]);

  useEffect(() => {
    if (!selectedSlot) return;
    const trimmed = driverSearchQuery.trim();
    if (trimmed.length < 2) {
      setDriverSearchResults([]);
      setDriverSearchError('');
      return;
    }
    setDriverSearchLoading(true);
    setDriverSearchError('');
    const timeoutId = window.setTimeout(async () => {
      try {
        const resp = await driversService.getDrivers({
          searchQuery: trimmed,
          page: 0,
          pageSize: 10,
          applicationStatus: null,
          driverState: null
        });
        if (resp.success && resp.data) {
          setDriverSearchResults(resp.data);
        } else {
          setDriverSearchError(resp.error || t('slots.driverSearchError'));
        }
      } catch {
        setDriverSearchError(t('slots.driverSearchError'));
      } finally {
        setDriverSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [driverSearchQuery, selectedSlot]);

  const fetchSlots = async () => {
    setLoading(true);
    setPageError('');
    try {
      const resp = await slotsService.listSlots();
      if (resp.success && resp.data) {
        setSlots(resp.data);
      } else {
        setPageError(resp.error || t('slots.fetchError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSlotDrivers = async (slotId: number) => {
    setDriversLoading(true);
    try {
      const resp = await slotsService.getDriversForSlot(slotId);
      if (resp.success && resp.data) {
        setSlotDrivers(resp.data);
      }
    } finally {
      setDriversLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingSlot(null);
    setFormDate('');
    setFormFromTime('09:00');
    setFormToTime('18:00');
    setFormMinIncome('');
    setFormPlaces('');
    setFormError('');
    setShowFormModal(true);
  };

  const openEditModal = (slot: SlotResponse) => {
    setEditingSlot(slot);
    setFormDate(slot.date);
    setFormFromTime(epochToTimeInput(slot.fromTime));
    setFormToTime(epochToTimeInput(slot.toTime));
    setFormMinIncome(slot.minIncome.toString());
    setFormPlaces(slot.places.toString());
    setFormError('');
    setShowFormModal(true);
  };

  const handleFormSubmit = async () => {
    if (!formDate || !formFromTime || !formToTime || !formMinIncome || !formPlaces) {
      setFormError(t('slots.formValidationError'));
      return;
    }
    const minIncome = parseInt(formMinIncome);
    const places = parseInt(formPlaces);
    if (isNaN(minIncome) || minIncome <= 0) {
      setFormError(t('slots.invalidMinIncome'));
      return;
    }
    if (isNaN(places) || places <= 0) {
      setFormError(t('slots.invalidPlaces'));
      return;
    }

    const fromTime = timeInputToEpoch(formDate, formFromTime);
    const toTime = timeInputToEpoch(formDate, formToTime);
    if (toTime <= fromTime) {
      setFormError(t('slots.toTimeBeforeFromTime'));
      return;
    }

    setFormLoading(true);
    setFormError('');
    try {
      const payload = { date: formDate, fromTime, toTime, minIncome, places };
      const resp = editingSlot
        ? await slotsService.updateSlot(editingSlot.id, payload)
        : await slotsService.createSlot(payload);

      if (resp.success) {
        setShowFormModal(false);
        showSuccess(editingSlot ? t('slots.updateSuccess') : t('slots.createSuccess'));
        await fetchSlots();
      } else {
        setFormError(resp.error || t('slots.saveError'));
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (slotId: number) => {
    setDeletingSlotId(slotId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSlotId) return;
    setDeleteLoading(true);
    try {
      const resp = await slotsService.deleteSlot(deletingSlotId);
      if (resp.success) {
        setShowDeleteConfirm(false);
        if (selectedSlot?.id === deletingSlotId) setSelectedSlot(null);
        showSuccess(t('slots.deleteSuccess'));
        await fetchSlots();
      } else {
        setPageError(resp.error || t('slots.deleteError'));
        setShowDeleteConfirm(false);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddDriver = async (driverId: number) => {
    if (!selectedSlot) return;
    setDriverActionLoading(true);
    try {
      const resp = await slotsService.addDriverToSlot(selectedSlot.id, driverId);
      if (resp.success) {
        setDriverSearchQuery('');
        setDriverSearchResults([]);
        await fetchSlotDrivers(selectedSlot.id);
        await fetchSlots();
      } else {
        setDriverSearchError(resp.error || t('slots.addDriverError'));
      }
    } finally {
      setDriverActionLoading(false);
    }
  };

  const handleRemoveDriver = async (driverId: number) => {
    if (!selectedSlot) return;
    setDriverActionLoading(true);
    try {
      const resp = await slotsService.removeDriverFromSlot(selectedSlot.id, driverId);
      if (resp.success) {
        await fetchSlotDrivers(selectedSlot.id);
        await fetchSlots();
      } else {
        setDriverSearchError(resp.error || t('slots.removeDriverError'));
      }
    } finally {
      setDriverActionLoading(false);
    }
  };

  const getSlotStatus = (slot: SlotResponse): 'active' | 'past' | 'future' => {
    if (slot.date === todayStr) return 'active';
    if (slot.date < todayStr) return 'past';
    return 'future';
  };

  if (loading) return <div className="page-loading">{t('common.loading')}</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('slots.title')}</h1>
          <p className="page-subtitle">{t('slots.subtitle')}</p>
        </div>
        <button className="btn-primary" onClick={openCreateModal}>
          {t('slots.createSlot')}
        </button>
      </div>

      {pageError && <div className="error-message">{pageError}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Slots table */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {slots.length === 0 ? (
            <div className="empty-state">{t('slots.noSlots')}</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('common.date')}</th>
                  <th>{t('slots.timeWindow')}</th>
                  <th>{t('slots.minIncome')}</th>
                  <th>{t('slots.places')}</th>
                  <th>{t('slots.drivers')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {slots.map(slot => {
                  const status = getSlotStatus(slot);
                  const isSelected = selectedSlot?.id === slot.id;
                  return (
                    <tr
                      key={slot.id}
                      onClick={() => setSelectedSlot(isSelected ? null : slot)}
                      style={{
                        cursor: 'pointer',
                        opacity: status === 'past' ? 0.5 : 1,
                        backgroundColor: isSelected ? 'var(--color-primary-light, #e8f4fd)' : undefined
                      }}
                    >
                      <td><strong>{slot.date}</strong></td>
                      <td>{formatTime(slot.fromTime)} – {formatTime(slot.toTime)}</td>
                      <td>{formatMoney(slot.minIncome)}</td>
                      <td>{slot.driverCount} / {slot.places}</td>
                      <td>{slot.driverCount}</td>
                      <td>
                        <span className={`status-badge status-${status}`}>
                          {t(`slots.status.${status}`)}
                        </span>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => openEditModal(slot)}
                          style={{ marginRight: 8 }}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          className="btn-danger btn-sm"
                          onClick={() => handleDeleteClick(slot.id)}
                        >
                          {t('common.delete')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Driver management panel */}
        {selectedSlot && (
          <div style={{ width: 360, flexShrink: 0, border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, background: '#fff' }}>
            <h3 style={{ margin: '0 0 12px' }}>
              {t('slots.driversPanel')} — {selectedSlot.date}
            </h3>
            <p style={{ margin: '0 0 12px', color: '#666', fontSize: 13 }}>
              {selectedSlot.driverCount} / {selectedSlot.places} {t('slots.places')}
            </p>

            {/* Search */}
            <input
              type="text"
              className="form-input"
              placeholder={t('slots.searchDriver')}
              value={driverSearchQuery}
              onChange={e => setDriverSearchQuery(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            {driverSearchLoading && <div style={{ fontSize: 12, color: '#666' }}>{t('common.loading')}</div>}
            {driverSearchError && <div style={{ fontSize: 12, color: 'red' }}>{driverSearchError}</div>}
            {driverSearchResults.length > 0 && (
              <div style={{ border: '1px solid #e0e0e0', borderRadius: 4, marginBottom: 12, maxHeight: 180, overflowY: 'auto' }}>
                {driverSearchResults.map(d => (
                  <div
                    key={d.driver.id}
                    style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{d.driver.fullName}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{d.driver.phoneNumber}</div>
                    </div>
                    <button
                      className="btn-primary btn-sm"
                      onClick={() => handleAddDriver(d.driver.id)}
                      disabled={driverActionLoading}
                    >
                      {t('slots.add')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Current drivers */}
            <h4 style={{ margin: '12px 0 8px', fontSize: 14 }}>{t('slots.assignedDrivers')}</h4>
            {driversLoading ? (
              <div style={{ fontSize: 12, color: '#666' }}>{t('common.loading')}</div>
            ) : slotDrivers.length === 0 ? (
              <div style={{ fontSize: 12, color: '#999' }}>{t('slots.noDriversAssigned')}</div>
            ) : (
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {slotDrivers.map(d => (
                  <div
                    key={d.id}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{d.fullName}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{d.phone}</div>
                    </div>
                    <button
                      className="btn-danger btn-sm"
                      onClick={() => handleRemoveDriver(d.id)}
                      disabled={driverActionLoading}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={editingSlot ? t('slots.editSlot') : t('slots.createSlot')}
      >
        <div className="form-group">
          <label className="form-label">{t('common.date')}</label>
          <input
            type="date"
            className="form-input"
            value={formDate}
            onChange={e => setFormDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">{t('slots.fromTime')}</label>
          <input
            type="time"
            className="form-input"
            value={formFromTime}
            onChange={e => setFormFromTime(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">{t('slots.toTime')}</label>
          <input
            type="time"
            className="form-input"
            value={formToTime}
            onChange={e => setFormToTime(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">{t('slots.minIncome')} (so'm)</label>
          <input
            type="number"
            className="form-input"
            placeholder="e.g. 150000"
            value={formMinIncome}
            onChange={e => setFormMinIncome(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">{t('slots.places')}</label>
          <input
            type="number"
            className="form-input"
            placeholder="e.g. 10"
            value={formPlaces}
            onChange={e => setFormPlaces(e.target.value)}
          />
        </div>
        {formError && <div className="error-message">{formError}</div>}
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => setShowFormModal(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn-primary" onClick={handleFormSubmit} disabled={formLoading}>
            {formLoading ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={t('slots.deleteConfirmTitle')}
      >
        <p>{t('slots.deleteConfirmMessage')}</p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn-danger" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? t('common.loading') : t('common.delete')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Slots;
